import React, { useEffect, useState , useRef} from "react";
import {
  ArrowRight,
  Users,
  Target,
  Award,
  Heart,
  Mail,
  Lightbulb,
  Globe,
  Clock,
  Trophy,
  Theater,
  Brain,
  Compass,
  MessageCircle,
  HeartHandshake,
  GraduationCap,
  MapPin,
  CalendarDays,
} from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { plugin } from "postcss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const AnimatedNumber = ({ value, delay = 0 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      let start = 0;
      const end = parseInt(value.replace("+", ""));
      if (isNaN(end)) return;

      const totalMs = 1200;
      const step = end / (totalMs / 16);

      const interval = setInterval(() => {
        start += step;
        if (start >= end) {
          setCount(end);
          clearInterval(interval);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(interval);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <span>
      {count}
      {value.includes("+") && "+"}
    </span>
  );
};

/* ===========================
   Composants réutilisables
=========================== */

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "", ...props }) => (
  <motion.img
    src={src}
    alt={alt}
    className={className}
    whileHover={{ scale: 1.03 }}
    transition={{ duration: 0.3 }}
    {...props}
  />
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-block px-3 py-1 text-sm font-semibold rounded-md ${className}`}
  >
    {children}
  </span>
);

/* ===========================
   Mapping couleurs
=========================== */

const bgColorMap = {
  primary: "bg-primary text-white",
  secondary: "bg-secondary text-white",
  gold: "bg-gold text-black",
  "electric-blue": "bg-electric-blue text-black",
};

const textColorMap = {
  primary: "text-primary",
  secondary: "text-secondary",
  gold: "text-gold",
  "electric-blue": "text-electric-blue",
};

const dotColorMap = {
  primary: "bg-primary",
  secondary: "bg-secondary",
  gold: "bg-gold",
  "electric-blue": "bg-electric-blue",
};

/* ===========================
        PAGE ACTIVITÉS
=========================== */

export default function Activities() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentActivity, setCurrentActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const activitySectionRef = useRef(null);
useEffect(() => {
  const params = new URLSearchParams(window.location.search);

  if (params.get("activity") === "true") {
    setTimeout(() => {
      activitySectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 500);
  }
}, []);

  useEffect(() => {
    async function fetchActivity() {
      try {
        const res = await fetch("/api/activity?action=getCurrent");
        const data = await res.json();

        if (data.success && data.activity) {
          setCurrentActivity(data.activity);
        } else {
          setCurrentActivity(null);
        }
      } catch (error) {
        console.error("ACTIVITY ERROR:", error);
        setCurrentActivity(null);
      } finally {
        setActivityLoading(false);
      }
    }

    fetchActivity();
  }, []);
  const programs = [
    {
      title: t("activities.programs.tableTennis.title"),
      category: t("activities.programs.tableTennis.category"),
      duration: t("activities.programs.tableTennis.duration"),
      participants: t("activities.programs.tableTennis.participants"),
      description: t("activities.programs.tableTennis.description"),
      image: "/images/tennis.jpg",
      icon: Trophy,
      color: "primary",
      features: [
        t("activities.programs.tableTennis.features.training"),
        t("activities.programs.tableTennis.features.competitions"),
        t("activities.programs.tableTennis.features.coaching"),
        t("activities.programs.tableTennis.features.concentration"),
      ],
    },
    {
      title: t("activities.programs.theater.title"),
      category: t("activities.programs.theater.category"),
      duration: t("activities.programs.theater.duration"),
      participants: t("activities.programs.theater.participants"),
      description: t("activities.programs.theater.description"),
      image: "/images/theatre.webp",
      icon: Theater,
      color: "secondary",
      features: [
        t("activities.programs.theater.features.rolePlay"),
        t("activities.programs.theater.features.bodyExpression"),
        t("activities.programs.theater.features.showCreation"),
        t("activities.programs.theater.features.publicPerformances"),
      ],
    },
    {
      title: t("activities.programs.chess.title"),
      category: t("activities.programs.chess.category"),
      duration: t("activities.programs.chess.duration"),
      participants: t("activities.programs.chess.participants"),
      description: t("activities.programs.chess.description"),
      image: "/images/echecs.jpg",
      icon: Brain,
      color: "electric-blue",
      features: [
        t("activities.programs.chess.features.rules"),
        t("activities.programs.chess.features.advancedStrategies"),
        t("activities.programs.chess.features.tournaments"),
        t("activities.programs.chess.features.logic"),
      ],
    },
    {
      title: t("activities.programs.orientation.title"),
      category: t("activities.programs.orientation.category"),
      duration: t("activities.programs.orientation.duration"),
      participants: t("activities.programs.orientation.participants"),
      description: t("activities.programs.orientation.description"),
      image: "/images/orient.webp",
      icon: Compass,
      color: "gold",
      features: [
        t("activities.programs.orientation.features.schoolGuidance"),
        t("activities.programs.orientation.features.careerAdvice"),
        t("activities.programs.orientation.features.listening"),
        t("activities.programs.orientation.features.community"),
      ],
    },
  ];

  const workshops = [];

  const pastEvents = [
    {
      title: t("activities.pastEvents.items.communication.title"),
      date: t("activities.pastEvents.items.communication.date"),
      place: t("activities.pastEvents.items.communication.place"),
      attendees: "50+",
      image: "/images/cover.jpeg",
    },
    {
      title: t("activities.pastEvents.items.partnership.title"),
      date: t("activities.pastEvents.items.partnership.date"),
      place: t("activities.pastEvents.items.partnership.place"),
      attendees: "50+",
      image: "/images/b1.jpg",
    },
    {
      title: t("activities.pastEvents.items.activities.title"),
      date: t("activities.pastEvents.items.activities.date"),
      place: t("activities.pastEvents.items.activities.place"),
      attendees: "50+",
      image: "/images/events1.jpeg",
    },
    {
      title: t("activities.pastEvents.items.website.title"),
      date: t("activities.pastEvents.items.website.date"),
      place: t("activities.pastEvents.items.website.place"),
      attendees: "50+",
      image: "/images/siteweb.jpeg",
    },
    {
      title: t("activities.pastEvents.items.mjcc.title"),
      date: t("activities.pastEvents.items.mjcc.date"),
      place: t("activities.pastEvents.items.mjcc.place"),
      image: "/images/minis.jpeg",
    },
    {
      title: t("activities.pastEvents.items.theaterKaraoke.title"),
      date: t("activities.pastEvents.items.theaterKaraoke.date"),
      place: t("activities.pastEvents.items.theaterKaraoke.place"),
      attendees: "50+",
      image: "/images/kara.jpeg",
    },
    {
      title: t("activities.pastEvents.items.training.title"),
      date: t("activities.pastEvents.items.training.date"),
      place: t("activities.pastEvents.items.training.place"),
      attendees: "50+",
      image: "/images/communi.jpeg",
    },
    {
      title: t("activities.pastEvents.items.bookFair.title"),
      date: t("activities.pastEvents.items.bookFair.date"),
      place: t("activities.pastEvents.items.bookFair.place"),
      attendees: "20+",
      image: "/images/evnt4.jpeg",
    },
  ];
  const expressionSpaces = [
    {
      title: t("activities.expressionSpaces.discussion.title"),
      description: t("activities.expressionSpaces.discussion.description"),
      icon: MessageCircle,
    },
    {
      title: t("activities.expressionSpaces.guidance.title"),
      description: t("activities.expressionSpaces.guidance.description"),
      icon: Compass,
    },
    {
      title: t("activities.expressionSpaces.citizenDebates.title"),
      description: t("activities.expressionSpaces.citizenDebates.description"),
      icon: Users,
    },
    {
      title: t("activities.expressionSpaces.experienceSharing.title"),
      description: t(
        "activities.expressionSpaces.experienceSharing.description",
      ),
      icon: HeartHandshake,
    },
  ];

  return (
    <div className="min-h-screen bg-blue-50 overflow-x-hidden">
      <section className="py-20 bg-gradient-to-br from-primary/10 to-electric-blue/5">
        <div className="max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.7 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-black text-foreground mb-6">
              {t("activities.hero.title1")}{" "}
              <span className="text-primary">
                {t("activities.hero.highlight")}
              </span>
            </h1>

            <p className="font-paragraph text-xl text-foreground/80 max-w-4xl mx-auto">
              {t("activities.hero.description")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===============================
            PROGRAMMES PRINCIPAUX
      =============================== */}

      <section className="py-20 bg-background">
        <div className="max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-5xl font-bold text-foreground mb-6">
              {t("activities.mainPrograms.title1")}{" "}
              <span className="text-primary">
                {t("activities.mainPrograms.highlight")}
              </span>
            </h2>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("activities.mainPrograms.description")}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-y-12 gap-x-10  m-4">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-shadow h-full p-0 flex flex-col">
                  <div className="relative">
                    <Image
                      src={program.image}
                      alt={program.title}
                      className="w-full h-64 object-cover rounded-none"
                    />
                    <Badge
                      className={`absolute top-4 left-4 ${bgColorMap[program.color]}`}
                    >
                      {program.category}
                    </Badge>
                  </div>

                  <CardContent className="p-8 flex flex-col flex-1">
                    <div className="flex items-center mb-4">
                      <program.icon
                        className={`h-8 w-8 ${textColorMap[program.color]} mr-3`}
                      />
                      <h3 className="text-2xl font-bold">{program.title}</h3>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4 text-sm text-foreground/60">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" /> {program.duration}
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />{" "}
                        {program.participants}
                      </div>
                    </div>

                    <p className="text-foreground/80 mb-6">
                      {program.description}
                    </p>

                    <div className="mb-6">
                      <h4 className="font-semibold mb-3">
                        {" "}
                        {t("activities.mainPrograms.highlights")}
                      </h4>
                      <ul className="space-y-2">
                        {program.features.map((f, i) => (
                          <li
                            key={i}
                            className="flex items-center text-sm text-foreground/80"
                          >
                            <div
                              className={`w-2 h-2 rounded-full ${dotColorMap[program.color]} mr-3`}
                            />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-auto">
                      <div className="flex items-center justify-center">
                        <span
                          className="
          inline-flex items-center gap-2
          px-4 py-1.5
          rounded-full
          bg-muted/60
          text-xs font-medium
          text-foreground/60
          tracking-wide
        "
                        >
                          ✨ {t("activities.mainPrograms.proposedBy")}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* ===============================
      ACTIVITÉ À VENIR
=============================== */}

      <section className="py-20 px-6 bg-slate-50" ref={activitySectionRef}>
        <div className="max-w-6xl mx-auto">
          {/* TITLE */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wider mb-4">
              {t("activities.currentActivity.badge")}
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">
              {t("activities.currentActivity.title")}
            </h2>

            <p className="mt-3 text-slate-500 max-w-2xl mx-auto">
              {t("activities.currentActivity.description")}
            </p>
          </div>

          {/* LOADING */}
          {activityLoading && (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          )}

          {/* NO ACTIVITY */}
          {!activityLoading && !currentActivity && (
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-md border border-slate-100 p-10 text-center">
              <CalendarDays size={42} className="mx-auto mb-4 text-slate-400" />

              <h3 className="text-xl font-bold text-slate-800">
                {t("activities.currentActivity.noActivity")}
              </h3>

              <p className="text-sm text-slate-500 mt-2">
                {t("activities.currentActivity.noActivityDescription")}
              </p>
            </div>
          )}

          {/* ACTIVITY */}
          {!activityLoading && currentActivity && (
            <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
              {/* HEADER */}
              <div className="bg-gradient-to-br from-indigo-600 to-violet-700 text-white p-7 md:p-10">
                <div className="flex items-center gap-2 text-white/80 text-xs font-semibold uppercase tracking-wider mb-3">
                  <CalendarDays size={16} />
                  {t("activities.currentActivity.upcoming")}
                </div>

                <h3 className="text-2xl md:text-3xl font-extrabold">
                  {currentActivity.title}
                </h3>

                <p className="mt-3 text-white/80 leading-relaxed">
                  {currentActivity.description}
                </p>
              </div>

              {/* CONTENT */}
              <div className="p-7 md:p-10">
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {/* DATE */}
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <CalendarDays size={19} className="text-indigo-600" />
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 font-semibold uppercase">
                        {t("activities.currentActivity.date")}
                      </p>

                      <p className="text-sm font-bold text-slate-800">
                        {currentActivity.activity_date}
                      </p>
                    </div>
                  </div>

                  {/* TIME */}
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Clock size={19} className="text-indigo-600" />
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 font-semibold uppercase">
                        {t("activities.currentActivity.time")}
                      </p>

                      <p className="text-sm font-bold text-slate-800">
                        {currentActivity.activity_time}
                      </p>
                    </div>
                  </div>

                  {/* LOCATION */}
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <MapPin size={19} className="text-indigo-600" />
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 font-semibold uppercase">
                        {t("activities.currentActivity.location")}
                      </p>

                      <p className="text-sm font-bold text-slate-800">
                        {currentActivity.location}
                      </p>
                    </div>
                  </div>

                  {/* DEADLINE */}
                  <div className="flex items-center gap-3 p-4 rounded-2xl bg-slate-50">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Clock size={19} className="text-indigo-600" />
                    </div>

                    <div>
                      <p className="text-xs text-slate-400 font-semibold uppercase">
                        {t("activities.currentActivity.deadline")}
                      </p>

                      <p className="text-sm font-bold text-slate-800">
                        {new Date(
                          currentActivity.registration_deadline,
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* REGISTRATION STATUS */}

                {new Date(currentActivity.registration_deadline) >
                new Date() ? (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 rounded-2xl bg-indigo-50 border border-indigo-100">
                    <div>
                      <p className="text-sm font-bold text-indigo-800">
                        {t("activities.currentActivity.registrationOpen")}
                      </p>

                      <p className="text-xs text-indigo-600 mt-1">
                        {t("activities.currentActivity.registerBefore")}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/membre/activity/register/${currentActivity.id}`,
                        )
                      }
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-md transition"
                    >
                      {t("activities.currentActivity.register")}
                      <ArrowRight size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="p-5 rounded-2xl bg-orange-50 border border-orange-200 text-center">
                    <p className="text-sm font-bold text-orange-800">
                      {t("activities.currentActivity.registrationClosed")}
                    </p>

                    <p className="text-xs text-orange-600 mt-1">
                      {t("activities.currentActivity.closedDescription")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ===============================
              ATELIERS
      =============================== */}

      <section className="py-20 bg-foreground text-background">
        <div className="max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-5xl font-bold">
              {t("activities.upcoming.title1")}{" "}
              <span className="text-[var(--electric-blue)]">
                {" "}
                {t("activities.upcoming.highlight")}
              </span>
            </h2>

            <p className="font-paragraph text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("activities.upcoming.description")}
            </p>
          </motion.div>

          {/* <div className="grid md:grid-cols-2 gap-8">
            {workshops.map((w, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
              > 
                 <Card className="bg-background text-foreground p-6 h-full">
                  <CardContent>
                    <h3 className="font-heading text-xl font-bold mb-3">
                      {w.title}
                    </h3>

                    <div className="space-y-2 mb-4 text-sm text-foreground/60">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        {w.date} • {w.time}
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-2" />
                        {w.location}
                      </div>
                    </div>

                    <p className="font-paragraph text-foreground/80 mb-4">
                      {w.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-secondary">
                        {w.spots}
                      </span>
                      <Button className="bg-primary hover:bg-primary/90 text-white">
                        S’inscrire
                      </Button>
                    </div>
                  </CardContent>
                </Card> 
                

              </motion.div>
            ))}
          </div>*/}
          <Card className="max-w-xl mx-auto text-center p-10 bg-background">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-primary" />
            </div>

            <h3 className="text-2xl font-bold mb-4">
              {t("activities.upcoming.cardTitle")}
            </h3>

            <p className="text-foreground/70 leading-relaxed">
              {t("activities.upcoming.cardDescription")}
            </p>

            <p className="mt-6 text-sm text-foreground/50 italic">
              {t("activities.upcoming.announcement")}
            </p>
          </Card>
        </div>
      </section>

      <section className="py-20 bg-muted">
        <div className="max-w-[90rem] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-5xl font-bold text-foreground mb-6">
              {t("activities.expressionSection.title1")}{" "}
              <span className="text-primary">
                {t("activities.expressionSection.highlight1")}
              </span>{" "}
              <span className="text-secondary">
                {" "}
                {t("activities.expressionSection.highlight2")}
              </span>
            </h2>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("activities.expressionSection.description")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expressionSpaces.map((item, index) => (
              <div
                key={index}
                className="
        bg-background
        border border-border
        rounded-2xl
        p-8
        h-full
        flex flex-col
        text-center
        hover:shadow-xl
        transition
      "
              >
                <div
                  className="
          w-14 h-14
          mx-auto mb-5
          flex items-center justify-center
          rounded-full
          bg-primary/10
          text-primary
        "
                >
                  <item.icon className="w-7 h-7" />
                </div>

                <h3
                  className="
          text-lg
          font-semibold
          mb-3
          text-emerald-600
          tracking-wide
        "
                >
                  {item.title}
                </h3>

                <p
                  className="
          text-sm
          text-foreground/70
          leading-relaxed
          flex-grow
        "
                >
                  {item.description}
                </p>

                <p
                  className="
          mt-6
          text-xs
          text-foreground/40
          italic
        "
                >
                  {t("activities.expressionSection.proposedBy")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ===========================
                   PAST EVENTS
            ============================ */}
      <section className="py-20 bg-background">
        <div className="max-w-[100rem] mx-auto px-4 sm:px-6">
          <motion.div
            className="text-center mb-16"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-6">
              {t("activities.pastEvents.title1")}{" "}
              <span className="text-electric-blue">
                {" "}
                {t("activities.pastEvents.highlight")}
              </span>
            </h2>
            <p className="font-paragraph text-base sm:text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("activities.pastEvents.description")}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {pastEvents.map((event, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 p-0">
                  <div className="relative">
                    <Image
                      src={event.image}
                      alt={event.title}
                      className="w-full h-40 sm:h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 text-white">
                      <h3 className="font-heading font-bold text-base sm:text-lg mb-1">
                        {event.title}
                      </h3>
                      <p className="font-paragraph text-xs sm:text-sm opacity-90">
                        {event.date}
                      </p>
                      <p className="font-paragraph text-xs sm:text-sm opacity-90">
                        {event.place}
                      </p>
                      <p className="font-paragraph text-xs sm:text-sm opacity-90">
                        {event.attendees}{" "}
                        {t("activities.pastEvents.participants")}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog">
              <Button className="px-6 py-3 border border-foreground text-foreground hover:bg-foreground hover:text-background">
                {t("activities.pastEvents.button")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===========================
                      CTA
            ============================ */}
      <section className="py-20 bg-gradient-to-r from-secondary to-primary text-primary-foreground">
        <div className="max-w-[100rem] mx-auto px-4 sm:px-6 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
              {t("activities.cta.title")}
            </h2>
            <p className="font-paragraph text-base sm:text-xl mb-8 max-w-3xl mx-auto">
              {t("activities.cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="https://aleoa.vercel.app/members?join=true">
                <Button className="px-6 py-3 border border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  {t("activities.cta.becomeMember")}
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="px-6 py-3 border border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                  {t("activities.cta.contact")}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
